import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
    PlayCircle,
    Download,
    Upload,
    History,
    FileText,
    ShieldCheck,
    AppWindow,
    Info,
    Video
} from 'lucide-react';
import { useState } from 'react';
import axios from 'axios';

const NavItem = ({ to, icon, title, delay }) => {
    const IconComponent = icon;
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: delay * 0.1 }}
        >
            <Link to={to} className="group flex flex-col items-center justify-center p-4 bg-gray-800/20 backdrop-blur-lg border border-white/10 rounded-2xl aspect-square transition-all duration-300 hover:bg-cyan-500/10 hover:border-cyan-400/50 hover:shadow-2xl hover:shadow-cyan-500/10">
                <div className="p-3 bg-gray-700/50 rounded-full transition-all duration-300 group-hover:bg-cyan-500/20 mb-3">
                     <IconComponent className="h-8 w-8 text-cyan-400 transition-transform duration-300 group-hover:scale-110" />
                </div>
                <span className="font-semibold text-sm text-slate-200 group-hover:text-white transition-colors">{title}</span>
            </Link>
        </motion.div>
    );
};

const HomePage = () => {
    const { user, fetchUser } = useAuth();
    const [claiming, setClaiming] = useState(false);
    const [claimMsg, setClaimMsg] = useState('');

    const handleClaimBonus = async () => {
        setClaiming(true);
        setClaimMsg('');
        try {
            const res = await axios.post('/api/profile/claim-signup-bonus', {}, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setClaimMsg(res.data.msg || 'Signup bonus claimed!');
            await fetchUser();
        } catch (err) {
            setClaimMsg(err.response?.data?.msg || 'Could not claim bonus.');
        } finally {
            setClaiming(false);
        }
    };

    const navItems = [
        { to: '/start', icon: PlayCircle, title: 'Start' },
        { to: '/withdraw', icon: Download, title: 'Withdraw' },
        { to: '/deposit', icon: Upload, title: 'Deposit' },
        { to: '/rules', icon: FileText, title: 'Rules' },
        { to: '/transactions', icon: History, title: 'Transactions' },
        { to: '/about', icon: Info, title: 'About' },
    ];

    return (
        <>
        <Helmet>
            <title>Dashboard | RatingApp — Earn USDT by Rating Apps</title>
            <meta name="description" content="Access your RatingApp dashboard. Start rating apps, track your balance, deposit or withdraw USDT, and manage your daily earning tasks." />
        </Helmet>
        <div className="w-full text-white font-sans p-4">
            <div className="max-w-2xl mx-auto pt-8 pb-16">
                <motion.div
                    className="mb-10"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <h1 className="text-3xl font-bold text-slate-200">
                        Hi, <span className="text-cyan-400">{user?.name || 'Welcome'}</span>
                    </h1>
                    <p className="text-slate-400">What would you like to do today?</p>
                    {!user?.signupBonusClaimed && (
                        <div className="mt-6 flex flex-col items-center">
                            <button
                                onClick={handleClaimBonus}
                                disabled={claiming}
                                className="px-6 py-3 w-full rounded-xl bg-gradient-to-r from-yellow-400 to-orange-400 text-slate-900 font-bold text-lg shadow-lg border-2 border-yellow-400/30 hover:scale-105 transition-all duration-300 disabled:opacity-60"
                            >
                                {claiming ? 'Claiming...' : '🎁 Claim Signup Bonus (30 USDT)'}
                            </button>
                            {claimMsg && (
                                <div className="mt-3 px-4 py-2 rounded-lg bg-yellow-500/10 text-yellow-300 font-semibold text-base border border-yellow-400/30">
                                    {claimMsg}
                                </div>
                            )}
                        </div>
                    )}
                </motion.div>

                <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-3 gap-4">
                    {navItems.map((item, index) => (
                        <NavItem key={item.title} {...item} delay={index} />
                    ))}
                </div>
            </div>
        </div>
        </>
    );
};

export default HomePage;
